import * as React from "react";
import { connect } from "react-redux";
import { v1 as uuidv1 } from "uuid";
import AppUtils, { LOADING_STATE } from "../utils/AppUtils";
import { isEmpty as _isEmpty } from "lodash";
import { IAppState } from "../models/IAppState";
import * as RouteConfigs from "../routes";
import {
    ORION_IMG_URL,
    toastNotificationAppearance,
    FeatureConfigValues,
    PrescriptionTabModes
} from "../utils/AppConstants";
import ITreatmentPlanTemplateResponseObj, {
    ISuggestionType,
    ITreatmentPlanTemplateSaveObj
} from "../models/ITreatmentPlanTemplateResponse";
import { TreatmentPlanActions } from "../actions/TreatmentPlanActions";
// import TreatmentPlanTemplateEditor from "../components/TreatmentPlanTemplateEditor";
import { IDoctor } from "@curefit/care-common";
import IAppointmentSchedule from "../models/IAppointmentSchedule";
import { ModalComponent } from "../components/ModalComponent";
import TreatmentPlanReviewPdf from "../components/TreatmentPlanReviewPdf";
import { IConstituentTemplate, IPdfTemplates } from "../models/PdfTemplatesModels";
import { EHRTreatmentPlanRequestMapper } from "../api/EhrTreatmentPlanRequestMapper";
import { OngoingConsultationActions } from "../actions/OngoingConsultationActions";
import { EventNames, SectionContext, UnregisterKeys, FlowContext } from "../utils/EventTrackerConstants";
import { Tracker } from "../utils/Tracker";
import LocalStorageUtils from "../utils/LocalStorageUtils";
import CommonPatientPrescriptionTab from "../components/ongoing/CommonPatientPrescriptionTab";
import { IConfigBasedPrescription } from "../models/patients/IConfigBasedPrescription";
import { EHRRequestMapper } from "../api/EHRRequestMapper";
import Prescription from "../components/templates/Prescription";

export interface CreateTreatmentPlanContainerRouteProps {
    params: {
        templateId: string,
        consultationId: string,
        patientId: string,
    },
    route: {
        path: string
    }
}

interface CreateTreatmentPlanContainerProps
    extends CreateTreatmentPlanContainerRouteProps {
}

type Props = ReturnType<typeof mapStateToProps> & ReturnType<typeof mapDispatchToProps> & CreateTreatmentPlanContainerProps

const CreateTreatmentPlanContainer: React.FC<Props> = (props) => {

    const [consultationDetailsLoadingState, setConsultationDetailsLoadingState] = React.useState<LOADING_STATE>(LOADING_STATE.NOT_INITIALIZED)
    const [uiConfiguration, setUiConfiguration] = React.useState<any>()
    const [suggestions, setSuggestions] = React.useState<ISuggestionType>()
    const [treatmentPlans, setTreamentPlans] = React.useState<ISuggestionType>()
    const [currentTemplate, setCurrentTemplate] = React.useState<ITreatmentPlanTemplateResponseObj>()
    const [pdfTemplates, setPdfTemplates] = React.useState<IPdfTemplates>(null)
    const [showPdf, setShowPdf] = React.useState<boolean>(false)

    const isTreatmentPlanEnabled = React.useRef(
        props.featureConfigInfo["OrionEnablePrescriptionTemplate"]
            ? props.featureConfigInfo["OrionEnablePrescriptionTemplate"] === FeatureConfigValues.TRUE
            : false
    )

    React.useEffect(() => {
        // On Mount
        if (!isTreatmentPlanEnabled.current) {
            RouteConfigs.RouteUtils.navigateToUrl("/")
        }
        window.onbeforeunload = function (e: any) {
            let dialogText = "Are you sure?";
            e.returnValue = dialogText;
            return dialogText;
        };

        Tracker.register({
            flow: EventNames.Treatment_Plan,
            treatmentPlanId: props.params.templateId ? props.params.templateId : uuidv1(),
            prescriptionType: props.params.templateId ? EventNames.Edit : EventNames.Add_New
        })

        getConsultationPdfTemplates()
        props.fetchTreatmentPlanDetails(props.doctorProfile.primarySubServiceType.code)
            .then((response: any) => {
                setUiConfiguration(response)
                fetchTemplateDetails()
            })
            .catch(() =>
                console.error("Error in fetching UI configuration for Treatment Plan")
            )

        // Unmount
        return () => {
            window.onbeforeunload = null;
            Tracker.unregister(UnregisterKeys.flow)
            Tracker.unregister(UnregisterKeys.treatmentPlanId)
            Tracker.unregister(UnregisterKeys.prescriptionType)
        }
    }, []);

    React.useEffect(() => {
        getConsultationTreatmentPlan()
    }, [currentTemplate])

    const getConsultationPdfTemplates = () => {
        props.fetchConsultationPdfTemplates(props.doctorProfile.id, props.doctorProfile.agentCenterMapping[0].centerId, props.doctorProfile.primarySubServiceType.code)
            .then((response: IConstituentTemplate) => {
                const templateIdToNameMap = AppUtils.setPdfTemplatesIdToNameMap(response.constituentTemplates || []);
                const templateNameToDataMap = AppUtils.setPdfTemplateNameToDataMap(response.constituentTemplates || []);
                const responseToSave = { ...response, templateIdToNameMap, templateNameToDataMap };
                setPdfTemplates(responseToSave)
            })
            .catch(() => {
                setPdfTemplates(null)
            });
    }

    const parseRecommendedTemplateResposne = (prescription: any): ITreatmentPlanTemplateResponseObj => {
        let parsedData: ITreatmentPlanTemplateResponseObj = {
            updatedAt: null,
            id: undefined,
            doctorId: props.doctorProfile.id,
            templateName: undefined,
            diagnosis: [],
            template: prescription && prescription.newConfigData
        }
        return parsedData;
    }

    const fetchTemplateDetails = () => {
        const patientId: number = Number(props.params.patientId);
        const templateId: number = Number(props.params.templateId)
        if (props.params.templateId && !patientId) {
            props.fetchTreatmentPlanById(props.params.templateId)
                .then((response: ITreatmentPlanTemplateResponseObj) => {
                    if (getDoctorId() !== response.doctorId) {
                        navigateToTreatmentPlans();
                    }
                    else {
                        setCurrentTemplate(response);
                        fetchSubSectionSuggestions();
                    }
                })
                .catch(() => {
                    console.error("Error fetching the existing data")
                })
        } else if (patientId && templateId) {
            props.getPrescriptionByReferenceId(patientId, templateId, props.doctorProfile.primarySubServiceType.code).then((prescription: any) => {
                console.log(prescription);
                const parsedData:ITreatmentPlanTemplateResponseObj = parseRecommendedTemplateResposne(prescription);
                if (getDoctorId() !== parsedData.doctorId) {
                    navigateToTreatmentPlans();
                }
                else {
                    setCurrentTemplate(parsedData);
                    fetchSubSectionSuggestions();
                }
            }).catch(err => {
                console.log(err);
            })
        }
        else if (props.params.consultationId && props.params.patientId) {
            let treatmentPlan = {} as ITreatmentPlanTemplateResponseObj
            props.fetchConsultationDetails(props.params.patientId, props.params.consultationId, props.doctorProfile.primarySubServiceType.code)
                .then((response: any) => {
                    const prescription = !_isEmpty(response)
                        ? EHRRequestMapper.getTemplateDataObjectToSave({ currentConsultationData: response }, "")
                        : {} as ITreatmentPlanTemplateResponseObj;
                    treatmentPlan = { ...prescription, id: undefined }
                })
                .finally(() => {
                    setCurrentTemplate(treatmentPlan)
                    fetchSubSectionSuggestions()
                })
        }
        else {
            setCurrentTemplate({} as ITreatmentPlanTemplateResponseObj)
            fetchSubSectionSuggestions()
        }
    }

    const fetchSubSectionSuggestions = () => {
        props.fetchSuggestionsForSubSections(props.doctorProfile.id, props.doctorProfile.primarySubServiceType.code)
            .then((response: { data: ISuggestionType }) => {
                setSuggestions((suggestions) => ({ ...suggestions, ...response.data }));
            })
            .catch(() => {
                console.error("Error in fetching suggestions for subsections")
            })
            .finally(() => {
                setConsultationDetailsLoadingState(LOADING_STATE.LOADED)
            })
    }

    const setDoctorRecommendedSuggestions = (recommendations: ISuggestionType) => {
        setSuggestions((suggestions) => ({ ...suggestions, ...recommendations }));
    }

    const getConsultationTreatmentPlan = () => {
        if (!_isEmpty(currentTemplate)) {
            let diagnosisArray: Array<string> = [];
            (currentTemplate.diagnosis || []).map((diagnosis: string) => {
                diagnosisArray = [...diagnosisArray, diagnosis]
            })
            if (diagnosisArray.length > 0) {
                props.fetchDataOnSelect(getDoctorId(), diagnosisArray)
                    .then((response: Array<ITreatmentPlanTemplateResponseObj>) => {
                        setTreamentPlans({
                            treatmentPlan: response.filter((resp: ITreatmentPlanTemplateResponseObj) => (resp.id !== currentTemplate.id))
                        })
                    })
                    .catch(() => {
                        console.error("Error fetching treatment plan templates")
                    })
            }
            else {
                setTreamentPlans({
                    treatmentPlan: []
                })
            }
        }
    }

    const navigateToTreatmentPlans = () => {
        props.clearOngoingConsultationDetails()
        RouteConfigs.RouteUtils.navigateToHrefUrl(null, RouteConfigs.TREATMENT_PLANS)
    }

    const getDoctorId = (): number => {
        return props.doctorProfile.id;
    }

    const updateCurrentTreatmentPlanData = (template: ITreatmentPlanTemplateSaveObj) => {
        setCurrentTemplate({ ...currentTemplate, ...template })
    }

    const saveTemplate = (template: ITreatmentPlanTemplateSaveObj) => {
        LocalStorageUtils.setClickEvents({ field: EventNames.Save, section: EventNames.Treatment_Plan, templateName: template.templateName })
        setCurrentTemplate({ ...currentTemplate, ...template })
        setShowPdf(true)
    }

    const handleSaveClick = () => {
        const { createdAt, updatedAt, id, ...tempalteSaveObj } = currentTemplate
        props.saveTreatmentPlan({ ...tempalteSaveObj, doctorId: getDoctorId() })
            .then(() => {
                AppUtils.triggerToastNotification(`Successfully ${props.params.templateId ? "edited" : "created"} the Treatment Plan: ${currentTemplate.templateName}`, toastNotificationAppearance.success)
                navigateToTreatmentPlans()
            })
            .catch(() => {
                console.error("Error saving the template")
            })
    }

    const onCancel = () => {
        LocalStorageUtils.setClickEvents({ field: EventNames.Cancel, section: EventNames.Treatment_Plan })
        navigateToTreatmentPlans()
    }

    const fetchDataOnSelect = (diagnosis?: Array<string>) => {
        if (diagnosis && diagnosis.length > 0) {
            props.fetchDataOnSelect(getDoctorId(), diagnosis)
                .then((response: Array<ITreatmentPlanTemplateResponseObj>) => {
                    setTreamentPlans({ treatmentPlan: response })
                })
                .catch(() => {
                    console.error("Error in fetching treatment plans for diagnosis")
                })
        }
        else {
            setTreamentPlans({ treatmentPlan: [] })
        }
    }

    return (
        <div>
            {Boolean(consultationDetailsLoadingState === LOADING_STATE.LOADED && !_isEmpty(pdfTemplates)) ?
                (
                    <FlowContext.Provider value={EventNames.Treatment_Plan}>
                        <SectionContext.Provider value={EventNames.Prescription}>
                            <div className="ongoing-consultation-cont">
                                <div className="ongoing-consultation-wrapper">
                                    <div className="ongoing-consultation-topnav treatment-plan-header">
                                        <div className="patient-info">
                                            <span className="patient-name">
                                                <img className="create-treatment-plan-header-image" src="/images/icons/treatmentPlanHeader.svg" height={18} width={18} />
                                                {`${props.params.templateId ? "EDIT" : "CREATE"} TEMPLATE`}</span>
                                        </div>
                                    </div>
                                </div>
                                <div className="ongoing-consultation-main-cont">
                                    <div className={"consultation-details-container"}>
                                        <div className="tab-content edit-prescription">
                                            <CommonPatientPrescriptionTab
                                                patientId={null}
                                                consultationId={null}
                                                consultationType={null}
                                                patientVitals={null}
                                                patientAllergies={null}
                                                patientChiefComplaints={null}
                                                patientRedFlags={null}
                                                patientHistory={null}
                                                prescriptionMedicationList={null}
                                                prescription={null}
                                                saveEditedPrescription={null}
                                                reviewPrescription={null}
                                                staticSpecialities={props.staticSpecialities}
                                                staticMedicineTypes={null}
                                                fetchStaticDiseaseData={null}
                                                fetchStaticLabTestsData={null}
                                                fetchStaticLifeStyleData={null}
                                                doctorType={null}
                                                updatePhysiotherapyPlanPdf={null}
                                                uploadPdf={null}
                                                currentMedicalProfile={null}
                                                doctorProfile={null}
                                                uiConfiguration={uiConfiguration}
                                                nextTab={null}
                                                savePatientMedicalProfile={null}
                                                saveEditedMedicalProfile={null}
                                                activeMedicines={null}
                                                updateAppointmentStatus={null}
                                                bookingId={null}
                                                suggestions={{ ...suggestions, ...treatmentPlans }}
                                                curefitUserId={null}
                                                metricIds={null}
                                                fetchPatientVitals={null}
                                                updateCurrentConsultationData={null}
                                                fetchDataOnSelect={fetchDataOnSelect}
                                                featureConfigInfo={props.featureConfigInfo}
                                                appointmentSchedule={{ id: -1000, doctor: { id: props.doctorProfile.id } as IDoctor, doctorType: props.doctorProfile.primarySubServiceType.code } as IAppointmentSchedule}
                                                prescriptionMode={PrescriptionTabModes.TREATMENT_PLAN}
                                                currentTemplate={currentTemplate}
                                                updateCurrentTreatmentPlanData={updateCurrentTreatmentPlanData}
                                                saveTemplate={saveTemplate}
                                                onCancel={onCancel}
                                                editMode={AppUtils.isDefined(props.params.templateId)}
                                                isRecoSaveMode={AppUtils.isDefined(props.params.patientId) && AppUtils.isDefined(props.params.templateId)}
                                                excludeWidgets={["reasonForVisit", "chiefComplaints"]}
                                                setDoctorRecommendedSuggestions={setDoctorRecommendedSuggestions}
                                            />
                                        </div>
                                        {showPdf &&
                                            <ModalComponent onClickCloseHandler={() => setShowPdf(false)} modalSize={"full"}>
                                                <TreatmentPlanReviewPdf
                                                    previewMode={false}
                                                    templateData={currentTemplate}
                                                    toggleModal={setShowPdf}
                                                    pdfTemplates={pdfTemplates}
                                                    staticSpecialistData={props.staticSpecialities}
                                                    handleSaveClick={handleSaveClick}
                                                    doctorProfile={props.doctorProfile}
                                                />
                                            </ModalComponent>
                                        }
                                    </div>
                                </div>
                            </div>
                        </SectionContext.Provider>
                    </FlowContext.Provider>
                )
                :
                (
                    <div className="central-loader ongoing-consultation-cont-loader">
                        <img src={ORION_IMG_URL + "loader/curefit-loader.gif"} />
                    </div>
                )
            }
        </div>
    );
}

const mapStateToProps = (state: IAppState) => {
    return {
        doctorProfile: state.doctorProfile,
        staticSpecialities: state.allStaticDataNew.doctorSpecialityResponseList,
        ongoingConsultation: state.ongoingConsultation,
        featureConfigInfo: state.featureConfigInfo
    }
}

const mapDispatchToProps = (dispatch: any) => {
    return {
        fetchTreatmentPlanById: (templateId: string): Promise<ITreatmentPlanTemplateResponseObj> => {
            return TreatmentPlanActions.fetchTreatmentPlansById(templateId)
        },
        fetchTreatmentPlanDetails: (doctorType: string): Promise<any> => {
            return TreatmentPlanActions.fetchTreatmentPlanDetails(doctorType)
        },
        fetchSuggestionsForSubSections: (doctorId: number, doctorType: string): Promise<ISuggestionType> => {
            return TreatmentPlanActions.fetchSuggestionsForSubSections(doctorId, doctorType)
        },
        fetchDataOnSelect: (doctorId: number, diagnosis: Array<string>): Promise<Array<ITreatmentPlanTemplateResponseObj>> => {
            return TreatmentPlanActions.fetchTreatmentPlanSuggestions(doctorId, diagnosis)
        },
        saveTreatmentPlan: (template: ITreatmentPlanTemplateSaveObj): Promise<any> => {
            return TreatmentPlanActions.saveTreatmentPlan(template)
        },
        fetchConsultationPdfTemplates: (doctorId: number, centerId: number, doctorType: string): Promise<IConstituentTemplate> => {
            return TreatmentPlanActions.fetchConsultationPdfTemplates(doctorId, centerId, doctorType)
        },
        clearOngoingConsultationDetails: () => {
            return dispatch(
                OngoingConsultationActions.clearOngoingConsultationDetails()
            );
        },
        fetchConsultationDetails: (patientId: string, appointmentId: string, subServiceType: string): Promise<IConfigBasedPrescription> => {
            return TreatmentPlanActions.fetchConsultationDetails(patientId, appointmentId, subServiceType);
        },
        getPrescriptionByReferenceId: (patientId: number, appointmentId: number, doctorType: string): Promise<any> => OngoingConsultationActions.getPrescriptionByReferenceId(patientId, appointmentId, doctorType)
    }
}


export default connect<ReturnType<typeof mapStateToProps>, ReturnType<typeof mapDispatchToProps>, CreateTreatmentPlanContainerProps>(
    mapStateToProps,
    mapDispatchToProps
)(CreateTreatmentPlanContainer)